import {
	Button,
	Dialog,
	DialogActions,
	DialogBody,
	DialogContent,
	DialogSurface,
	DialogTitle,
	InputOnChangeData,
	makeStyles,
	SearchBox,
	SearchBoxChangeEvent,
	Spinner,
} from '@fluentui/react-components';
import { NotificationToastDuration, NotificationType, useFabricContext } from '@idmc-ms-fabric/integration';
import { CoreAttr, CoreClz } from '@infa-hawk/models';
import { ServiceRegistry } from '@infa-hawk/services';
import { contentPublishApi, PublishRequestElementType, PublishRequestIdentityType, PublishRequestOperation } from '@infa-service/mdp-content';
import React, { FC, useState } from 'react';

import constants from '../utils/constants';
import { getFabricUniqueSearchText } from '../utils/infa-utils';

const useStyles = makeStyles({
	cntr: {
		padding: '1rem',
	},
	header: {
		fontSize: '2rem',
		lineHeight: '2.5rem',
		marginBottom: '2rem',
	},
	field: {
		display: 'flex',
		width: '30rem',
		alignItems: 'center',
		marginBottom: '0.5rem',
	},
	label: {
		marginRight: '1rem',
		fontWeight: 'bold',
		flex: `1 1 40%`,
	},
	value: {
		flex: `1 1 60%`,
		border: '1px solid #ccc',
		padding: '5px',
		borderRadius: '0.25rem',
		marginRight: '1rem',
	},
	list: {
		'listStyle': 'none',
		'padding': 0,
		'margin': 0,
		'& > li': {
			'lineHeight': '1.5rem',
			'padding': '5px 10px',
			'borderBottom': '1px solid #ccc',
			'cursor': 'pointer',
			'&:hover': {
				backgroundColor: '#f0f0f0',
			},
		},
	},
	link: {
		justifyContent: 'flex-end',
		marginTop: '1rem',
	},
	dialogHeader: {
		display: 'flex',
		justifyContent: 'space-between',
	},
});

const type = 'data-warehouse';

const payload = {
	from: 0,
	size: 1000,
	query: {
		bool: {
			must: [
				{
					terms: {
						elementType: ['OBJECT'],
					},
				},
			],

			filter: [
				{
					terms: {
						[CoreAttr.ResourceType]: ['Fabric Data Warehouse'],
					},
				},
				{
					terms: {
						[CoreAttr.ClassType]: ['core.Resource'],
					},
				},
			],
		},
	},
	post_filter: {
		bool: {
			filter: [],
		},
	},
	sort: [
		{
			[CoreAttr.Name]: {
				order: 'asc',
			},
		},
	],
};

const LinkWarehouseAndCS: FC = () => {
	const classes = useStyles();
	const [warehouse, setWarehouse] = useState<{ name: string; id: string; workspaceId: string }>();
	const [catalogSource, setCatalogSource] = useState<{ name: string; id: string }>();
	const [open, setOpen] = React.useState(false);
	const { extensionClient } = useFabricContext();
	const [searchList, setSearchList] = useState<Array<{ name: string; id: string }>>();

	const pickWH = async () => {
		const selection = await extensionClient.datahub.openDialog({
			workspaceNavigationEnabled: true,
			supportedTypes: [type],
			dialogDescription: 'Pick a warehouse',
		});

		if (selection && selection.selectedDatahubItem && selection.selectedDatahubItem.length > 0) {
			const item = selection.selectedDatahubItem[0];
			setWarehouse({ name: item.datahubItemUI?.displayName || item.itemObjectId, id: item.itemObjectId, workspaceId: item.workspaceObjectId });
		}
	};

	const makeSearchCall = async () => {
		const { search } = ServiceRegistry.get();
		const result = await search.elasticService.execute(payload);
		if (result.hits) {
			if (result.hits.length === 0) {
				setSearchList(undefined);
			} else {
				setSearchList(result.hits.map((item) => ({ name: item[CoreAttr.Name], id: item[CoreAttr.Identity] })));
			}
		}
	};

	const [linking, setLinking] = useState(false);
	const link = async () => {
		setLinking(true);
		try {
			if (warehouse && catalogSource) {
				const fabricSelectedArtifactURI = getFabricUniqueSearchText({
					workspaceId: warehouse.workspaceId,
					artifactType: type,
					artifactId: warehouse.id,
				});
				const response = await contentPublishApi.publishContent({
					xINFAPRODUCTID: import.meta.env.VITE_APP_CLIENT_ID,
					bulkPublishRequest: {
						items: [
							{
								elementType: PublishRequestElementType.Object,
								operation: PublishRequestOperation.Update,
								type: CoreClz.Resource,
								identity: catalogSource?.id,
								identityType: PublishRequestIdentityType.Internal,
								attributes: {
									[constants.EXTERNAL_URI]: [fabricSelectedArtifactURI],
								},
								attributeUpdateOperationTypeMap: {
									[constants.EXTERNAL_URI]: 'OVERWRITE',
								},
							},
						],
					},
				});

				if (response && response.items && response.items[0] && response.items[0].messageCode === 'CONTENT_SUCCESS') {
					extensionClient.notification.open({
						title: `Linked Successfully`,
						message: `'${catalogSource.name}' linked with '${warehouse.name}'`,
						notificationType: NotificationType.Success,
						duration: NotificationToastDuration.Medium,
					});
				} else {
					extensionClient.notification.open({
						title: `Linking failed`,
						message: `Error while linking '${catalogSource?.name}' with '${warehouse?.name}'`,
						notificationType: NotificationType.Error,
						duration: NotificationToastDuration.Medium,
					});
				}
			}
		} catch (error) {
			extensionClient.notification.open({
				title: `Linking failed`,
				message: `Error while linking '${catalogSource?.name}' with '${warehouse?.name}'`,
				notificationType: NotificationType.Error,
				duration: NotificationToastDuration.Medium,
			});
		} finally {
			setLinking(false);
		}
	};

	const [filter, setFilter] = useState('');
	const onChangeSearch = (ev: SearchBoxChangeEvent, data: InputOnChangeData) => {
		console.log('data', data.value);
		setFilter(data.value);
	};

	return (
		<div className={classes.cntr}>
			<div className={classes.header}>Link warehouse with CS</div>
			<div className={classes.field}>
				<div className={classes.label}>Warehouse :</div>
				<div className={classes.value}>
					<span>{warehouse?.name || '-'}</span>
				</div>
				<Button onClick={pickWH}>Select</Button>
			</div>
			<div className={classes.field}>
				<div className={classes.label}>Catalog Source :</div>
				<div className={classes.value}>
					<span>{catalogSource?.name || '-'}</span>
				</div>
				<Button
					onClick={() => {
						makeSearchCall();
						setCatalogSource(undefined);
						setOpen(true);
					}}
				>
					Select
				</Button>
			</div>

			<Dialog open={open} onOpenChange={() => {}}>
				<DialogSurface>
					<DialogBody>
						<DialogTitle>
							<div className={classes.dialogHeader}>
								<div>Pick a catalog source</div> <SearchBox appearance="outline" onChange={onChangeSearch} />
							</div>
						</DialogTitle>
						<DialogContent>
							{searchList === undefined ? (
								<Spinner />
							) : (
								<ul className={classes.list}>
									{searchList
										.filter((item) => item.name.toLocaleLowerCase().includes(filter))
										.map((item) => (
											<li key={item.id} onClick={() => setCatalogSource(item)}>
												{item.name}
											</li>
										))}
								</ul>
							)}
							{searchList && searchList.length === 0 && <div>No catalog sources found</div>}
						</DialogContent>
						<DialogActions>
							<Button appearance="secondary" onClick={() => setOpen(false)}>
								Close
							</Button>
							<Button
								disabled={catalogSource === undefined}
								appearance="primary"
								onClick={() => {
									setOpen(false);
								}}
							>
								Select
							</Button>
						</DialogActions>
					</DialogBody>
				</DialogSurface>
			</Dialog>

			<div className={[classes.field, classes.link].join('')}>
				<Button appearance="primary" disabled={linking === true || warehouse === undefined || catalogSource === undefined} onClick={link}>
					{linking && (
						<>
							<Spinner size="tiny" />
							&nbsp;
						</>
					)}
					Link
				</Button>
			</div>
		</div>
	);
};

export default LinkWarehouseAndCS;
